import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import Loading from "../../../Components/Loading/Loading";
import styles from "./newbenf.module.css";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Calendar2Fill } from "react-bootstrap-icons";
import { LoadingButton } from "@mui/lab";
import { MobileDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { getEntityByProperty } from "../../../utils/Filters";

dayjs.extend(isSameOrAfter);

function NewBeneficiary() {
  // utils
  const theme = useTheme();
  const navigate = useNavigate();
  const [initialLoading, setInitialLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  //Datafields
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [appliesTo, setAppliesTo] = useState("");
  const [benefitType, setBenefitType] = useState("percentage");
  const [benefitAmount, setBenefitAmount] = useState(0);
  const [recurringType, setRecurringType] = useState("كل مشاهدة");

  // Relations
  const [availableSites, setAvailableSites] = useState([]);
  const [availablePayments, setAvailablePayments] = useState([]);
  const _types = ["شريك", "وسيلة دفع", "موقع"];

  const fetchData = useCallback(async () => {
    setInitialLoading(true);
    try {
      const [sites, payments] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/sites/`, {
          withCredentials: true,
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/paymentmethod/`, {
          withCredentials: true,
        }),
      ]);
      setAvailableSites(sites.data);
      setAvailablePayments(payments.data);
      setInitialLoading(false);
    } catch (error) {
      setInitialLoading(false);
      toast.error(error.message || "خطأ أثناء تحميل المعلومات اللازمة.");
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleNameChange = (e) => setName(e.target.value);
  const handleTypeChange = (e) => setType(e.target.value);
  const handleAppliesToChange = (e) => setAppliesTo(e.target.value);
  const handleBenefitAmountChange = (e) => {
    let newAmount = e.target.value;

    // Reject invalid characters (allow digits and at most one dot)
    if (!/^\d*\.?\d*$/.test(newAmount)) {
      return;
    }

    // Allow user to clear input or type just a dot
    if (newAmount === "" || newAmount === ".") {
      setBenefitAmount(newAmount);
      return;
    }

    let numericValue = parseFloat(newAmount);

    if (isNaN(numericValue)) {
      return;
    }

    // Handle clamping and decimal formatting
    if (benefitType === "percentage") {
      if (numericValue > 100) numericValue = 100;
      if (numericValue < 0) numericValue = 0;

      newAmount = numericValue.toFixed(1);
    } else {
      newAmount = numericValue.toFixed(2);
    }

    setBenefitAmount(newAmount);
  };

  const handleStartDateChange = (newStartDate) => {
    setStartDate(newStartDate);

    // If endDate exists and newStartDate >= endDate, reset endDate
    if (endDate && dayjs(newStartDate).isSameOrAfter(dayjs(endDate), "day")) {
      setEndDate(null);
    }
  };

  const handleEndDateChange = (newEndDate) => {
    setEndDate(newEndDate);
  };

  const handleSubmit = () => {
    setLoading(true);
    // Reset Errors
    const newErrors = {};

    // Validation
    if (!name.trim()) newErrors.name = "الإسم مطلوب.";
    if (!type.trim()) newErrors.type = "الإسم مطلوب.";
    if (!startDate) newErrors.startDate = "تاريخ بداية الإستفادة مطلوب.";
    if (!benefitType) newErrors.benefitType = "نوع الربح مطلوب.";
    if (!recurringType)
      newErrors.recurringType = "يجب تحديد نمط تكرار الإستفادة.";
    if (benefitType === "percentage") {
      if (benefitAmount === 0 || benefitAmount < 0)
        newErrors.benefitAmount = "يجب إدخال نسبة مئوية صحيحة.";
    }
    if (benefitType === "fixedAmount") {
      if (benefitAmount === 0) newErrors.benefitAmount = "قيمة الربح مطلوبة.";
    }
    if (type !== "شريك") {
      if (!appliesTo) newErrors.appliesTo = "يجب تحديد الجهة المستفيدة.";
    }
    // If there are errors, set them and prevent submission
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    let newBenf = {
      name: name,
      type: type,
      benefitRule: {
        type: benefitType,
        fixedAmount: benefitType === "percentage" ? null : benefitAmount,
        percentage: benefitType === "percentage" ? benefitAmount : null,
        appliesToSiteId:
          type === "موقع"
            ? getEntityByProperty("name", availableSites, appliesTo)?.id
            : null,
        appliesToPaymentId:
          type === "وسيلة دفع"
            ? getEntityByProperty("name", availablePayments, appliesTo)?.id
            : null,
        recurringType: recurringType,
        startDate: startDate,
        endDate: endDate,
      },
    };

    if (newBenf) {
      console.log(newBenf);
      axios
        .post(`${process.env.REACT_APP_API_URL}/beneficiaries/new/`, newBenf, {
          withCredentials: true,
        })
        .then((res) => {
          setLoading(false);
          navigate("/app/beneficiaries/");
          toast.success("تم إنشاء المستفيد الجديد بنجاح!");
        })
        .catch((err) => {
          setLoading(false);
          console.log(err);
          toast.error(
            err.response?.data?.error ||
              "حدثت مشكلة أثناء إنشاء المستفيد الجديد، حاول مرة أخرى."
          );
        });
    }
  };
  return initialLoading ? (
    <Loading />
  ) : (
    <div>
      <div className={styles.form}>
        <div className={styles.container}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              onClick={() => navigate(-1)}
              variant="contained"
              sx={{
                width: "48px",
                height: "48px",
                padding: 0,
                margin: 0,
                minWidth: "auto",
                marginRight: "12px",
              }}
            >
              <ArrowRight size={24} />
            </Button>
            <Typography
              variant="h4"
              color={theme.palette.neutral[900]}
              fontWeight={700}
            >
              إضافة مستفيد جديد
            </Typography>
          </div>
          <div className={styles.basicDetails}>
            <div className={styles.formSection}>
              <Typography
                variant="h5"
                color={theme.palette.neutral[800]}
                fontWeight={600}
                marginBottom={2}
              >
                المعلومات الأساسية
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={(2, 3)}>
                <FormControl
                  fullWidth
                  sx={{ flex: "1 1 45%", minWidth: "250px" }}
                >
                  <TextField
                    fullWidth
                    label="الإسم"
                    type="text"
                    value={name}
                    onChange={handleNameChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    slotProps={{
                      htmlInput: {
                        maxLength: 24,
                      },
                      input: {
                        endAdornment: name && (
                          <InputAdornment position="end">
                            {name.length}/{24}
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </FormControl>
                <FormControl
                  fullWidth
                  sx={{ flex: "1 1 45%", minWidth: "250px" }}
                  error={!!errors.userRole}
                >
                  <InputLabel id="role-label">النوع</InputLabel>
                  <Select
                    fullWidth
                    labelId="role-label"
                    label="النوع"
                    value={type}
                    onChange={handleTypeChange}
                  >
                    {_types.length > 0 ? (
                      _types.map((t, index) => (
                        <MenuItem key={index} value={t}>
                          {t}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>
                        لا يوجد خيارات
                      </MenuItem>
                    )}
                  </Select>
                  <FormHelperText>{errors.type}</FormHelperText>
                </FormControl>
                {type === "موقع" || type === "وسيلة دفع"
                  ? (() => {
                      const config = {
                        موقع: {
                          label: "الموقع المستفيد",
                          options: availableSites,
                        },
                        "وسيلة دفع": {
                          label: "وسيلة الدفع المستفيدة",
                          options: availablePayments,
                        },
                      }[type];

                      return (
                        <FormControl
                          fullWidth
                          sx={{ flex: "1 1 45%", minWidth: "250px" }}
                          error={!!errors.userRole}
                        >
                          <InputLabel id="role-label">
                            {config.label}
                          </InputLabel>
                          <Select
                            fullWidth
                            labelId="role-label"
                            label={config.label}
                            value={appliesTo}
                            onChange={handleAppliesToChange}
                          >
                            {config.options.length > 0 ? (
                              config.options.map((item, index) => (
                                <MenuItem key={index} value={item.name}>
                                  {item.name}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem value="" disabled>
                                لا يوجد خيارات
                              </MenuItem>
                            )}
                          </Select>
                          <FormHelperText>{errors.type}</FormHelperText>
                        </FormControl>
                      );
                    })()
                  : null}
              </Box>
            </div>

            <div className={styles.formSection}>
              <Typography
                variant="h5"
                color={theme.palette.neutral[800]}
                fontWeight={600}
                marginBottom={2}
              >
                كيفية الاستفادة
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={(2, 3)}>
                <FormControl
                  sx={{ flex: "1 1 45%", minWidth: "250px" }}
                  error={!!errors.date}
                >
                  <MobileDatePicker
                    localeText={{
                      clearButtonLabel: "مسح",
                      okButtonLabel: "تم",
                      todayButtonLabel: "اليوم",
                      toolbarTitle: "إختار التاريخ",
                    }}
                    timeZone="UTC"
                    label="تاريخ بداية الإستفادة"
                    format="YYYY/MM/DD"
                    value={startDate}
                    onChange={handleStartDateChange}
                    slotProps={{
                      textField: {
                        error: !!errors.date,
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <Calendar2Fill />
                            </InputAdornment>
                          ),
                        },
                      },
                      actionBar: {
                        actions: ["today", "clear", "accept"],
                      },
                    }}
                  />
                  <FormHelperText>{errors.date}</FormHelperText>
                </FormControl>
                <FormControl
                  sx={{ flex: "1 1 45%", minWidth: "250px" }}
                  error={!!errors.date}
                >
                  <MobileDatePicker
                    localeText={{
                      clearButtonLabel: "مسح",
                      okButtonLabel: "تم",
                      todayButtonLabel: "اليوم",
                      toolbarTitle: "إختار التاريخ",
                    }}
                    timeZone="UTC"
                    label="تاريخ نهاية الإستفادة"
                    format="YYYY/MM/DD"
                    value={endDate}
                    onChange={handleEndDateChange}
                    {...(startDate
                      ? { minDate: dayjs(startDate).add(1, "day") }
                      : {})}
                    slotProps={{
                      textField: {
                        error: !!errors.date,
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <Calendar2Fill />
                            </InputAdornment>
                          ),
                        },
                      },
                      actionBar: {
                        actions: ["today", "clear", "accept"],
                      },
                    }}
                  />
                  <FormHelperText>
                    اذا كانت الاستفادة مستمرة لا تقم بادخال تاريخ انتهاء.
                  </FormHelperText>
                </FormControl>
              </Box>
              <Box
                display={"flex"}
                gap={(2, 3)}
                flexWrap="wrap"
                justifyContent={"space-between"}
                sx={{
                  padding: "16px 0",
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <FormControl sx={{ flex: "1 1 45%", minWidth: "250px" }}>
                  <FormLabel id="options-label">نوع الربح</FormLabel>
                  <RadioGroup
                    value={benefitType}
                    name="radio-buttons-group"
                    onChange={(e) => setBenefitType(e.target.value)}
                    row
                  >
                    <FormControlLabel
                      value="percentage"
                      control={<Radio />}
                      label="% نسبة مئوية"
                    />
                    <FormControlLabel
                      value="fixedAmount"
                      control={<Radio />}
                      label="مبلغ ثابت"
                    />
                  </RadioGroup>
                </FormControl>
                <FormControl>
                  <FormLabel id="options-label"> تكرار الاستفادة</FormLabel>
                  <RadioGroup
                    value={recurringType}
                    name="radio-buttons-group"
                    onChange={(e) => setRecurringType(e.target.value)}
                    row
                  >
                    <FormControlLabel
                      value="كل مشاهدة"
                      control={<Radio />}
                      label="كل مشاهدة"
                    />
                    <FormControlLabel
                      value="كل شهر"
                      control={<Radio />}
                      label="كل شهر"
                    />
                    <FormControlLabel
                      value="كل سنة"
                      control={<Radio />}
                      label="كل سنة"
                    />
                  </RadioGroup>
                </FormControl>
                <FormControl
                  fullWidth
                  sx={{ flex: "1 1 45%", minWidth: "250px" }}
                >
                  <TextField
                    fullWidth
                    label="قيمة الربح"
                    type="text"
                    value={benefitAmount}
                    onChange={handleBenefitAmountChange}
                    // error={!!errors[formIdentifier]?.amount}
                    // helperText={errors[formIdentifier]?.amount}
                    // disabled={!discounted}
                  />
                </FormControl>
              </Box>
            </div>
          </div>
          <LoadingButton
            variant="contained"
            loading={loading}
            color="primary"
            sx={{ fontSize: "20px" }}
            fullWidth
            onClick={handleSubmit}
          >
            إضافة
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}

export default NewBeneficiary;

// import React, { useCallback, useEffect, useState } from "react";
// import {
//   Box,
//   Button,
//   Chip,
//   FormControl,
//   FormHelperText,
//   IconButton,
//   InputAdornment,
//   InputLabel,
//   MenuItem,
//   OutlinedInput,
//   Select,
//   TextField,
//   Typography,
//   useTheme,
// } from "@mui/material";
// import styles from "./newuser.module.css";
// import axios from "axios";
// import {
//   ArrowRight,
//   EyeFill,
//   EyeSlashFill,
//   KeyFill,
// } from "react-bootstrap-icons";
// import { useNavigate } from "react-router-dom";
// import { useSelector } from "react-redux";
// import toast from "react-hot-toast";
// import Loading from "../../../Components/Loading/Loading";
// import generator from "generate-password-browser";
// import { LoadingButton } from "@mui/lab";
// function NewUser() {
//   // utils
//   const theme = useTheme();
//   const [initialLoading, setInitialLoading] = useState(false);
//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);

//   // Data Fields
//   const [name, setName] = useState("");
//   const [phoneNum, setPhoneNum] = useState("");
//   const [username, setUsername] = useState("");
//   const [password, setPassword] = useState("");
//   const [userRole, setUserRole] = useState("");
//   const [worktime, setWorktime] = useState("");
//   const [sites, setSites] = useState([]);
//   const [availableWorkTimes, setAvailableWorkTimes] = useState([]);
//   const [availableSites, setAvailableSites] = useState([]);
//   const [availableRoles, setAvailableRoles] = useState([]);
//   const [showPassword, setShowPassword] = React.useState(false);
//   const checkRole = useSelector((state) => state.auth.role.roleName);
//
//   const filteredRoles =
//     checkRole === "Admin"
//       ? availableRoles.filter((role) => role.name === "Employee")
//       : availableRoles;

//   function getEntityID(data, entity) {
//     const found = data.find((e) => e.name === entity);
//     return found ? found.id : null;
//   }
//   function getWorkTimeId(data, entity) {
//     const found = data.find((e) => e.duration === entity);
//     return found ? found.id : null;
//   }

//   const handleNameChange = (e) => {
//     setName(e.target.value);
//   };
//   const handlePhoneNumChange = (e) => {
//     setPhoneNum(e.target.value);
//   };
//   const handleUsernameChange = (e) => {
//     setUsername(e.target.value);
//   };
//   const handlePasswordChange = (e) => {
//     setPassword(e.target.value);
//   };

//   const handleClickShowPassword = () => setShowPassword((show) => !show);
//   const handleWorkTimeChange = (e) => {
//     setWorktime(e.target.value);
//   };

//   const handleRoleChange = (e) => {
//     const role = e.target.value;

//     setSites((prev) => {
//       if (role !== "SuperAdmin") {
//         // If previous value was an array, take the first element as a string
//         return Array.isArray(prev) && prev.length > 0 ? prev[0] : "";
//       } else {
//         // Ensure the value is always an array
//         return Array.isArray(prev) ? prev : prev ? [prev] : [];
//       }
//     });

//     setUserRole(role);
//   };

//   const handleSiteChange = (e) => {
//     setSites(e.target.value);
//   };

//   const ITEM_HEIGHT = 48;
//   const ITEM_PADDING_TOP = 8;
//   const MenuProps = {
//     PaperProps: {
//       style: {
//         maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
//         width: 250,
//       },
//     },
//   };

//   const handleSubmit = () => {
//     setLoading(true);
//     // Reset errors
//     const newErrors = {};

//     // Validation checks
//     if (!name.trim()) newErrors.name = "الإسم مطلوب.";
//     if (!phoneNum.trim() || !/^\d+$/.test(phoneNum))
//       newErrors.phoneNum = "رقم الهاتف مطلوب.";
//     if (!username.trim()) newErrors.username = "إسم المستخدم مطلوب.";
//     if (!password.trim() || password.length < 6)
//       newErrors.password = "كلمة السر يجب أن تكون على الأقل ستة أحرف.";
//     if (!userRole) newErrors.userRole = "دور المستخدم مطلوب.";
//     if (!worktime) newErrors.worktime = "مدة العمل مطلوبة.";
//     if (!sites.length) newErrors.sites = "الموقع مطلوب.";

//     // If there are errors, set them and prevent submission
//     if (Object.keys(newErrors).length > 0) {
//       setErrors(newErrors);
//       setLoading(false);
//       return;
//     }

//     const sitesIds = Array.isArray(sites)
//       ? sites.map((site) => getEntityID(availableSites, site))
//       : [getEntityID(availableSites, sites)];

//     const newUser = {
//       name: name,
//       phoneNumber: phoneNum,
//       username: username,
//       password: password,
//       RoleId: getEntityID(availableRoles, userRole),
//       WorkTimeId: getWorkTimeId(availableWorkTimes, worktime),
//       SiteIds: sitesIds,
//     };
//     setErrors({});

//     if (newUser) {
//       axios
//         .post(`${process.env.REACT_APP_API_URL}/user/`, newUser, {
//           withCredentials: true,
//         })
//         .then((res) => {
//           setLoading(false);
//           navigate("/app/users/");
//           toast.success("تم إنشاء المستخدم الجديد بنجاح!");
//         })
//         .catch((err) => {
//           setLoading(false);
//           toast.error(
//             err.response?.data?.error ||
//               "حدثت مشكلة أثناء إنشاء المستخدم الجديد، حاول مرة أخرى."
//           );
//         });
//     }
//   };

//   return initialLoading ? (
//     <Loading />
//   ) : (
//     <div>
//       <div className={styles.form}>
//         <div className={styles.container}>
//           <div style={{ display: "flex", alignItems: "center" }}>
//             <Button
//               onClick={() => navigate(-1)}
//               variant="contained"
//               sx={{
//                 width: "48px",
//                 height: "48px",
//                 padding: 0,
//                 margin: 0,
//                 minWidth: "auto",
//                 marginRight: "12px",
//               }}
//             >
//               <ArrowRight size={24} />
//             </Button>
//             <Typography
//               variant="h4"
//               color={theme.palette.neutral[900]}
//               fontWeight={700}
//             >
//               إضافة مستخدم جديد
//             </Typography>
//           </div>
//           <div className={styles.basicDetails}>
//             <div className={styles.formSection}>
//               <Typography
//                 variant="h5"
//                 color={theme.palette.neutral[800]}
//                 fontWeight={600}
//                 marginBottom={2}
//               >
//                 المعلومات الأساسية
//               </Typography>
//               <Box display="flex" flexWrap="wrap" gap={(2, 3)}>
//                 {/* View Quantity Field */}
//                 <FormControl
//                   fullWidth
//                   sx={{ flex: "1 1 45%", minWidth: "250px" }}
//                 >
//                   <TextField
//                     fullWidth
//                     label="الإسم"
//                     type="text"
//                     value={name}
//                     onChange={handleNameChange}
//                     error={!!errors.name}
//                     helperText={errors.name}
//                     slotProps={{
//                       htmlInput: {
//                         maxLength: 24,
//                       },
//                       input: {
//                         endAdornment: name && (
//                           <InputAdornment position="end">
//                             {name.length}/{24}
//                           </InputAdornment>
//                         ),
//                       },
//                     }}
//                   />
//                 </FormControl>

//                 {/* View Type Field */}
//                 <FormControl
//                   fullWidth
//                   sx={{ flex: "1 1 45%", minWidth: "250px" }}
//                 >
//                   <TextField
//                     fullWidth
//                     label="رقم الهاتف"
//                     type="tel"
//                     value={phoneNum}
//                     onChange={handlePhoneNumChange}
//                     error={!!errors.phoneNum}
//                     helperText={errors.phoneNum}
//                   />
//                 </FormControl>
//               </Box>
//             </div>
//             <div className={styles.formSection}>
//               <Typography
//                 variant="h5"
//                 color={theme.palette.neutral[800]}
//                 fontWeight={600}
//                 marginBottom={2}
//               >
//                 حساب المستخدم
//               </Typography>
//               <Box display="flex" flexWrap="wrap" gap={(2, 3)}>
//                 {/* View Quantity Field */}
//                 <FormControl
//                   fullWidth
//                   sx={{ flex: "1 1 45%", minWidth: "250px" }}
//                 >
//                   <TextField
//                     fullWidth
//                     label="اسم المستخدم"
//                     type="text"
//                     value={username}
//                     onChange={handleUsernameChange}
//                     error={!!errors.username}
//                     helperText={errors.username}
//                     slotProps={{
//                       htmlInput: {
//                         maxLength: 24,
//                       },
//                       input: {
//                         endAdornment: username && (
//                           <InputAdornment position="end">
//                             {username.length}/{24}
//                           </InputAdornment>
//                         ),
//                       },
//                     }}
//                   />
//                 </FormControl>
//                 <FormControl
//                   sx={{ flex: "1 1 45%", minWidth: "250px" }}
//                   error={!!errors.password}
//                   variant="outlined"
//                 >
//                   <InputLabel htmlFor="outlined-adornment-password">
//                     كلمة السر
//                   </InputLabel>
//                   <OutlinedInput
//                     id="outlined-adornment-password"
//                     type={showPassword ? "text" : "password"}
//                     endAdornment={
//                       <InputAdornment position="end">
//                         <IconButton
//                           aria-label={"Generate Password"}
//                           onClick={generatePassword}
//                           edge="end"
//                         >
//                           <KeyFill />
//                         </IconButton>
//                         <IconButton
//                           aria-label={
//                             showPassword
//                               ? "hide the password"
//                               : "display the password"
//                           }
//                           onClick={handleClickShowPassword}
//                           edge="end"
//                           sx={{ marginLeft: "8px" }}
//                         >
//                           {showPassword ? <EyeSlashFill /> : <EyeFill />}
//                         </IconButton>
//                       </InputAdornment>
//                     }
//                     label="Password"
//                     value={password}
//                     onChange={handlePasswordChange}
//                   />
//                   <FormHelperText>{errors.password}</FormHelperText>
//                 </FormControl>
//               </Box>
//             </div>
//             <div className={styles.formSection}>
//               <Typography
//                 variant="h5"
//                 color={theme.palette.neutral[800]}
//                 fontWeight={600}
//                 marginBottom={2}
//               >
//                 معلومات العمل
//               </Typography>
//               <Box display="flex" flexWrap="wrap" gap={(2, 3)}>
//                 {/* View Type Field */}
//                 <FormControl
//                   fullWidth
//                   sx={{ flex: "1 1 45%", minWidth: "250px" }}
//                   error={!!errors.userRole}
//                 >
//                   <InputLabel id="role-label">دور المستخدم</InputLabel>
//                   <Select
//                     fullWidth
//                     labelId="role-label"
//                     label="دور المستخدم"
//                     value={userRole}
//                     onChange={handleRoleChange}
//                   >
//                     {availableRoles.length > 0 ? (
//                       filteredRoles.map((role, index) => (
//                         <MenuItem key={index} value={role.name}>
//                           {role.name}
//                         </MenuItem>
//                       ))
//                     ) : (
//                       <MenuItem value="" disabled>
//                         لا يوجد خيارات
//                       </MenuItem>
//                     )}
//                   </Select>
//                   <FormHelperText>{errors.userRole}</FormHelperText>
//                 </FormControl>
//                 {/* View Quantity Field */}

//                 <FormControl
//                   fullWidth
//                   sx={{ flex: "1 1 45%", minWidth: "250px" }}
//                   error={!!errors.worktime}
//                 >
//                   <InputLabel id="role-label">مدة عمل المستخدم</InputLabel>
//                   <Select
//                     fullWidth
//                     labelId="worktime-label"
//                     label="مدة عمل المستخدم"
//                     value={worktime}
//                     onChange={handleWorkTimeChange}
//                   >
//                     {availableWorkTimes.length > 0 ? (
//                       availableWorkTimes.map((worktime, index) => (
//                         <MenuItem key={index} value={worktime.duration}>
//                           {worktime.duration}
//                         </MenuItem>
//                       ))
//                     ) : (
//                       <MenuItem value="" disabled>
//                         لا يوجد خيارات
//                       </MenuItem>
//                     )}
//                   </Select>
//                   <FormHelperText>{errors.worktime}</FormHelperText>
//                 </FormControl>
//                 {/* Date & Time Picker */}
//                 <FormControl
//                   sx={{ flex: "1 1 45%", minWidth: "250px" }}
//                   error={!!errors.sites}
//                 >
//                   <InputLabel id="demo-multiple-chip-label">
//                     موقع عمل المستخدم
//                   </InputLabel>
//                   <Select
//                     labelId="demo-multiple-chip-label"
//                     id="demo-multiple-chip"
//                     multiple={checkRole !== "Admin" && userRole !== "Employee"} // Ensure boolean logic matches
//                     value={
//                       checkRole !== "Admin" && userRole !== "Employee"
//                         ? Array.isArray(sites)
//                           ? sites
//                           : [] // Ensure array for multiple selection
//                         : typeof sites === "string"
//                         ? sites
//                         : "" // Ensure string for single selection
//                     }
//                     onChange={handleSiteChange}
//                     input={
//                       <OutlinedInput
//                         id="select-multiple-chip"
//                         label="موقع عمل المستخدم"
//                       />
//                     }
//                     renderValue={(selected) => {
//                       const selectedValues = Array.isArray(selected)
//                         ? selected
//                         : [selected];
//                       return (
//                         <Box
//                           sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
//                         >
//                           {selectedValues.map((value) => (
//                             <Chip key={value} label={value} />
//                           ))}
//                         </Box>
//                       );
//                     }}
//                     MenuProps={MenuProps}
//                   >
//                     {availableSites.length > 0 ? (
//                       availableSites.map((site) => (
//                         <MenuItem key={site.id} value={site.name}>
//                           {site.name}
//                         </MenuItem>
//                       ))
//                     ) : (
//                       <MenuItem value="" disabled>
//                         لا يوجد خيارات
//                       </MenuItem>
//                     )}
//                   </Select>
//                   <FormHelperText>{errors.sites}</FormHelperText>
//                 </FormControl>
//               </Box>
//             </div>
//           </div>
//           <LoadingButton
//             variant="contained"
//             loading={loading}
//             color="primary"
//             sx={{ fontSize: "20px" }}
//             fullWidth
//             onClick={handleSubmit}
//           >
//             إضافة
//           </LoadingButton>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default NewUser;
